/**
 * The feedback widget's non-AI fallback (Plan Phase 6c).
 *
 * `components/feedback-widget.tsx` catches a failed `POST /feedback/summarize`
 * and still files the ticket, carrying the reporter's own words through as the
 * summary. That behaviour is correct and this module preserves it verbatim —
 * a broken model must never cost somebody their bug report.
 *
 * What was wrong is that the fallback was SILENT. The catch block set
 * `aiGenerated: false`, submitted it to the API, and rendered nothing: the
 * review panel looked identical, Sparkles icon and all, whether the summary
 * had been written by a model or by the catch block. A reader could not tell a
 * degraded result from a real one, which is the exact failure
 * `components/ai/ai-degraded-notice.tsx` exists to forbid.
 *
 * Extracting the shape here does two things: it makes "the fallback still
 * carries the user's words" testable without mounting a 2,200-line widget, and
 * it gives `aiGenerated` one producer, so the flag that drives the notice
 * cannot drift from the flag that is submitted.
 */

/** Matches the widget's `tldrData` state shape. */
export interface FeedbackSummaryData {
  tldr: string;
  userStory?: string;
  acceptanceCriteria?: string[];
  category: string;
  priority: string;
  affectedArea: string;
  screenshotContext?: string;
  /** False ⇒ no model produced this. Drives the notice AND the submit payload. */
  aiGenerated: boolean;
}

/**
 * The widget truncates to 200 characters. Kept identical so the fallback is a
 * pure extraction rather than a behaviour change smuggled in beside one.
 */
export const FEEDBACK_FALLBACK_TLDR_LIMIT = 200;

/**
 * Build the summary used when AI assistance did not answer.
 *
 * The metadata values are the same neutral placeholders the widget already
 * used. They are NOT guesses dressed up as classification: "General",
 * "Medium" and "Unknown" are what an unclassified ticket looks like, and the
 * triage view reads `aiGenerated: false` to know they were never assessed.
 */
export function buildNonAiFallback(userFeedback: string): FeedbackSummaryData {
  return {
    tldr: userFeedback.slice(0, FEEDBACK_FALLBACK_TLDR_LIMIT),
    category: 'General',
    priority: 'Medium',
    affectedArea: 'Unknown',
    aiGenerated: false,
  };
}
